import { config } from '@iheartjane/config';
import { useAeropayUser } from '@iheartjane/data-access/react-query';
import type { FlagsType } from '@iheartjane/feature-flags';
import {
  getFlagContext,
  useFlagClient,
  useFlagsWithoutEvaluation,
} from '@iheartjane/feature-flags';
import {
  extractTrackingTags,
  featureFlagProperties,
  hasMixpanelDeviceIdPrefix,
  stripMixpanelDeviceIdPrefix,
} from '@iheartjane/tracking';
import type { AppMode } from '@iheartjane/types';
import { Storage, statePostalCode } from '@iheartjane/utils';
import forEach from 'lodash-es/forEach';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router';

import { initDmMixpanel } from '@jane/dm/internal';
import { useCustomerSelector } from '@jane/monolith/customer/redux';
import { selectApplication } from '@jane/monolith/customer/redux/application';
import { selectStore } from '@jane/monolith/customer/redux/store';
import { useUserPreferences } from '@jane/shared-ecomm/providers';
import { useMoengageIdentify } from '@jane/shared-ecomm/tracking';
import { getJwt } from '@jane/shared/auth';
import { useJaneUser } from '@jane/shared/data-access';

export interface IdentifyCustomerProps {
  app: AppMode;
  appStoreId?: number | string;
  brandPartnerId?: number | string;
  operatorId?: number | string;
  queryParams?: Record<string, string | undefined>;
}

export const IdentifyCustomer = ({
  app,
  appStoreId,
  brandPartnerId,
  operatorId,
  queryParams,
}: IdentifyCustomerProps) => {
  useMoengageIdentify();
  const flagClient = useFlagClient();
  const { store } = useCustomerSelector(selectStore);
  const { store_id } = useParams();
  const { data: janeUser } = useJaneUser();
  const user = janeUser?.user;
  const { janeDeviceId, mixpanelDistinctId } =
    useCustomerSelector(selectApplication);
  const { userLocation } = useUserPreferences();
  const signedIn = useRef(false);
  const featureFlags = useFlagsWithoutEvaluation();
  const routeStoreId = Number(store_id);
  const storeId =
    store?.id ?? (Number.isInteger(routeStoreId) ? routeStoreId : undefined);
  const state = statePostalCode(store?.state) || Storage.get('state') || '';
  const organizationId = store?.organization_id;
  const { data: aeropayData } = useAeropayUser({
    janeUserId: user?.id,
    jwt: getJwt(),
  });

  const aeropayAccountVerified = !!aeropayData?.bank_accounts.length;
  const firstBankAccountLinked = aeropayData?.bank_accounts.length === 1;
  const linkedAccountDate = Storage.get('aeropayLinkDate') || null;

  useEffect(() => {
    if (user?.id) {
      Storage.set('isJaneGoldMember', aeropayAccountVerified);
    }
  }, [aeropayAccountVerified, user]);

  useEffect(() => {
    if (firstBankAccountLinked) {
      if (!linkedAccountDate) {
        Storage.set('aeropayLinkDate', new Date());
      }
    }
  }, [firstBankAccountLinked, linkedAccountDate]);

  useEffect(() => {
    /**
     * NOTE(Kyrsten): This code is duplicated in components/application/customerAppContainer.tsx
     * in order to allow us to wait for the flagClient.identify call before rendering the app.
     *
     * Eventually we should move this out of IdentifyCustomer and into all the various app containers,
     * and remove the call here.
     */
    if (!janeDeviceId) return;
    flagClient?.identify(
      getFlagContext({
        extraAttributes: {
          appMode: app,
          storeId: storeId,
          state,
          organizationId: organizationId ?? undefined,
        },
        janeDeviceId,
        user,
      })
    );
  }, [app, flagClient, janeDeviceId, user, storeId, state, organizationId]);

  useEffect(() => {
    const trackingTags = extractTrackingTags(queryParams);
    const mappedFeatureFlags: FlagsType = {};
    Object.keys(featureFlags).forEach((flag) => {
      mappedFeatureFlags[`Flag_${flag}`] = featureFlags[flag];
    });

    if (user?.id) {
      signedIn.current = true;
      janeMixpanel.identify(user.id.toString());
      janeMixpanel.people.set({
        aeropayAccountVerified,
        $email: user.email,
        $last_login: new Date(),
        $first_name: user.first_name || user.nickname,
        $last_name: user.last_name,
        Nickname: user.nickname,
        Phone: user.phone,
        'User Id': user.id,
        cityState: userLocation.cityState,
        coordinates: userLocation.coordinates,
        featureFlags: featureFlagProperties(featureFlags),
        ...trackingTags,
      });
      janeMixpanel.people.set_once({
        aeropayLinkDate: linkedAccountDate,
      });

      // add first touch attribution tags to user profile
      const ogTrackingTags: Record<string, unknown> = {};
      forEach(
        trackingTags,
        (value, key) => (ogTrackingTags[key + ' [first touch]'] = value)
      );
      janeMixpanel.people.set_once(ogTrackingTags);
    } else {
      // Guard against wiping the embedding-app id we adopt below.
      if (signedIn.current) {
        janeMixpanel.reset();
        signedIn.current = false;
      }

      // Adopt the embedding app's distinct id (forwarded via postMessage) so an
      // embedded guest's events share identity with the parent instead of this
      // iframe's own auto-generated, cross-origin-partitioned id.
      if (mixpanelDistinctId && hasMixpanelDeviceIdPrefix(mixpanelDistinctId)) {
        // Anonymous parent: identify() rejects "$device:" ids, so adopt the
        // parent's device id directly. Setting both keeps the distinct id shown
        // on events identical and a later identify() merge consistent.
        janeMixpanel.register({
          $device_id: stripMixpanelDeviceIdPrefix(mixpanelDistinctId),
          distinct_id: mixpanelDistinctId,
        });
      } else if (mixpanelDistinctId) {
        // Identified parent: the forwarded id is a real user id, which identify
        // accepts and links via Mixpanel identity merge.
        janeMixpanel.identify(mixpanelDistinctId);
      }
    }

    // set utm tags as super properties to enable last touch attribution
    // https://help.mixpanel.com/hc/en-us/articles/360001337103-Last-Touch-UTM-Tags
    janeMixpanel.register({
      app,
      janeDeviceId,
      appStoreId: appStoreId || null,
      brandPartnerId: brandPartnerId || null,
      operatorId: operatorId || null,
      userCityState: userLocation.cityState,
      userCoordinates: userLocation.coordinates,
      userSegments: janeUser?.user_segments.length
        ? janeUser.user_segments.join(',')
        : null,
      build: config.buildVersion?.substring(0, 7),
      featureFlags: { ...mappedFeatureFlags },
      aeropayAccountVerified,
      aeropayLinkDate: linkedAccountDate,
      ...trackingTags,
    });
    initDmMixpanel({
      appMode: app,
      janeDeviceId,
      storeId: appStoreId ? Number(appStoreId) : null,
    });
  }, [
    aeropayAccountVerified,
    app,
    appStoreId,
    brandPartnerId,
    user,
    featureFlags,
    janeDeviceId,
    mixpanelDistinctId,
    linkedAccountDate,
    operatorId,
    queryParams,
    userLocation.cityState,
    userLocation.coordinates,
    janeUser?.user_segments,
  ]);

  return null;
};
